import { getChatAjaxConfig } from './chatSettingsAjaxConfig';
import { truncateSessionPreview } from '../helpers/sessionPreviewText';

const FEEDBACK_KEY = 'growtype_chat_backups_feedback';
const BACKUPS_HASH = '#account-settings-backups-content';

function getErrorMessage(response, fallback) {
    let parsedResponse = response?.responseJSON;

    if (!parsedResponse && typeof response?.responseText === 'string') {
        try {
            parsedResponse = JSON.parse(response.responseText);
        } catch (error) {
            // Non-JSON errors use the user-friendly fallback below.
        }
    }

    return parsedResponse?.data?.message || parsedResponse?.message || fallback;
}

function rememberFeedback(message) {
    try {
        window.sessionStorage.setItem(FEEDBACK_KEY, message);
    } catch (error) {
        // Storage can be unavailable in privacy-focused browser modes.
    }
}

function consumeFeedback() {
    try {
        const message = window.sessionStorage.getItem(FEEDBACK_KEY) || '';
        window.sessionStorage.removeItem(FEEDBACK_KEY);
        return message;
    } catch (error) {
        return '';
    }
}

function showBackupsTab($manager) {
    const tabTrigger = document.querySelector(`[data-bs-target="${BACKUPS_HASH}"]`);
    if (window.location.hash !== BACKUPS_HASH || !tabTrigger) {
        return;
    }

    if (window.bootstrap?.Tab) {
        window.bootstrap.Tab.getOrCreateInstance(tabTrigger).show();
    } else if (typeof jQuery(tabTrigger).tab === 'function') {
        jQuery(tabTrigger).tab('show');
    }

    $manager.closest('form').find('.row-submit').addClass('d-none');
}

function reloadBackupsTab(message) {
    rememberFeedback(message);
    if (window.location.hash !== BACKUPS_HASH) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${BACKUPS_HASH}`);
    }
    window.location.reload();
}

export function bindBackupsManager() {
    const $manager = jQuery('[data-backups-manager]');
    if (!$manager.length) {
        return;
    }

    const $feedback = $manager.find('.chat-settings-backups-feedback');
    const ajaxConfig = getChatAjaxConfig();
    const ajaxUrl = ajaxConfig.url;
    const nonce = ajaxConfig.nonce;
    const createAction = $manager.attr('data-create-action');
    const previewAction = $manager.attr('data-preview-action');
    const restoreAction = $manager.attr('data-restore-action');
    const deleteAction = $manager.attr('data-delete-action');
    const $previewModal = $manager.find('#growtypeChatBackupPreviewModal');
    const $restoreModal = $manager.find('#growtypeChatBackupRestoreModal');
    const $deleteModal = $manager.find('#growtypeChatBackupDeleteModal');
    let selectedBackupId = 0;
    let selectedDeleteBackupId = 0;

    const persistedFeedback = consumeFeedback();
    if (persistedFeedback) {
        $feedback.text(persistedFeedback).addClass('is-success');
    }

    showBackupsTab($manager);

    jQuery(document)
        .off('shown.bs.tab.chatBackups')
        .on('shown.bs.tab.chatBackups', '.chat-settings-page [data-bs-toggle="tab"]', function () {
            const isBackupsTab = jQuery(this).attr('data-bs-target') === BACKUPS_HASH;
            $manager.closest('form').find('.row-submit').toggleClass('d-none', isBackupsTab);
        });

    $manager
        .off('click.chatBackupsCreate', '.chat-settings-backup-create')
        .on('click.chatBackupsCreate', '.chat-settings-backup-create', function () {
            const $button = jQuery(this);
            const originalText = $button.text();
            $feedback.removeClass('is-success is-error').text('');
            $button.prop('disabled', true).text('Creating…');

            jQuery.ajax({
                url: ajaxUrl,
                method: 'POST',
                data: { action: createAction, nonce },
            }).done((response) => {
                reloadBackupsTab(response?.data?.message || 'Backup created.');
            }).fail((response) => {
                $feedback
                    .text(getErrorMessage(response, 'The backup could not be created.'))
                    .addClass('is-error');
                $button.prop('disabled', false).text(originalText);
            });
        });

    $manager
        .off('click.chatBackupsPreview', '.chat-settings-backup-preview')
        .on('click.chatBackupsPreview', '.chat-settings-backup-preview', function () {
            const $button = jQuery(this);
            const backupId = Number($button.attr('data-backup-id')) || 0;
            const $status = $previewModal.find('.chat-settings-backup-preview-status');
            const $list = $previewModal.find('.chat-settings-backup-preview-list');

            $previewModal.find('.chat-settings-backup-modal-date').text($button.attr('data-backup-date') || '');
            $previewModal.find('.chat-settings-backup-modal-summary').text($button.attr('data-backup-summary') || '');
            $status.removeClass('is-error').text('Loading chats…');
            $list.empty();

            if (window.bootstrap?.Modal) {
                window.bootstrap.Modal.getOrCreateInstance($previewModal.get(0)).show();
            } else if (typeof $previewModal.modal === 'function') {
                $previewModal.modal('show');
            }

            jQuery.ajax({
                url: ajaxUrl,
                method: 'POST',
                data: { action: previewAction, nonce, backup_id: backupId },
            }).done((response) => {
                const chats = Array.isArray(response?.data?.preview?.chats)
                    ? response.data.preview.chats
                    : [];
                $status.text(chats.length ? '' : 'No chats are stored in this backup.');

                chats.forEach((chat) => {
                    const messageCount = Number(chat?.message_count) || 0;
                    const messageLabel = messageCount === 1 ? 'message' : 'messages';
                    const $row = jQuery('<div class="g-chatsessions-single chat-settings-backup-preview-chat"></div>');
                    const $image = jQuery('<div class="g-chatsessions-single-image"></div>');
                    const $imageUser = jQuery('<div class="g-chatsessions-single-image-user"></div>');
                    if (chat?.image_url) {
                        $imageUser.css({
                            backgroundImage: `url(${JSON.stringify(chat.image_url)})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        });
                    }
                    $imageUser.appendTo($image);
                    $image.appendTo($row);

                    const $content = jQuery('<div class="g-chatsessions-single-content"></div>');
                    jQuery('<p class="g-chatsessions-single-content-title"></p>')
                        .text(chat?.name || 'Untitled chat')
                        .appendTo($content);
                    jQuery('<p class="g-chatsessions-single-content-desc"></p>')
                        .text(truncateSessionPreview(chat?.description || 'No messages yet.'))
                        .appendTo($content);
                    $content.appendTo($row);

                    const $actions = jQuery('<div class="g-chatsessions-single-actions"></div>');
                    jQuery('<span class="chat-settings-backup-preview-chat-meta"></span>')
                        .text(`${messageCount.toLocaleString()} ${messageLabel}`)
                        .appendTo($actions);
                    $actions.appendTo($row);

                    jQuery('<div class="g-chatsessions-single-time"></div>')
                        .text(chat?.time || '')
                        .appendTo($row);
                    $row.appendTo($list);
                });
            }).fail((response) => {
                $status
                    .text(getErrorMessage(response, 'The backup preview could not be loaded.'))
                    .addClass('is-error');
            });
        });

    $manager
        .off('click.chatBackupsRestore', '.chat-settings-backup-restore')
        .on('click.chatBackupsRestore', '.chat-settings-backup-restore', function () {
            const $button = jQuery(this);
            selectedBackupId = Number($button.attr('data-backup-id')) || 0;
            $restoreModal.find('.chat-settings-backup-modal-date').text($button.attr('data-backup-date') || '');
            $restoreModal.find('.chat-settings-backup-modal-summary').text($button.attr('data-backup-summary') || '');

            if (window.bootstrap?.Modal) {
                window.bootstrap.Modal.getOrCreateInstance($restoreModal.get(0)).show();
            } else if (typeof $restoreModal.modal === 'function') {
                $restoreModal.modal('show');
            }
        });

    $manager
        .off('click.chatBackupsConfirm', '.chat-settings-backup-confirm-restore')
        .on('click.chatBackupsConfirm', '.chat-settings-backup-confirm-restore', function () {
            if (!selectedBackupId) {
                return;
            }

            const $button = jQuery(this);
            const originalText = $button.text();
            $feedback.removeClass('is-success is-error').text('');
            $button.prop('disabled', true).text('Restoring…');

            jQuery.ajax({
                url: ajaxUrl,
                method: 'POST',
                data: { action: restoreAction, nonce, backup_id: selectedBackupId },
            }).done((response) => {
                reloadBackupsTab(response?.data?.message || 'Backup restored.');
            }).fail((response) => {
                $feedback
                    .text(getErrorMessage(response, 'The backup could not be restored.'))
                    .addClass('is-error');
                $button.prop('disabled', false).text(originalText);
            });
        });

    $manager
        .off('click.chatBackupsDelete', '.chat-settings-backup-delete')
        .on('click.chatBackupsDelete', '.chat-settings-backup-delete', function () {
            const $button = jQuery(this);
            selectedDeleteBackupId = Number($button.attr('data-backup-id')) || 0;
            $deleteModal.find('.chat-settings-backup-modal-date').text($button.attr('data-backup-date') || '');
            $deleteModal.find('.chat-settings-backup-modal-summary').text($button.attr('data-backup-summary') || '');

            if (window.bootstrap?.Modal) {
                window.bootstrap.Modal.getOrCreateInstance($deleteModal.get(0)).show();
            } else if (typeof $deleteModal.modal === 'function') {
                $deleteModal.modal('show');
            }
        });

    $manager
        .off('click.chatBackupsConfirmDelete', '.chat-settings-backup-confirm-delete')
        .on('click.chatBackupsConfirmDelete', '.chat-settings-backup-confirm-delete', function () {
            if (!selectedDeleteBackupId) {
                return;
            }

            const $button = jQuery(this);
            const originalText = $button.text();
            $feedback.removeClass('is-success is-error').text('');
            $button.prop('disabled', true).text('Deleting…');

            jQuery.ajax({
                url: ajaxUrl,
                method: 'POST',
                data: { action: deleteAction, nonce, backup_id: selectedDeleteBackupId },
            }).done((response) => {
                reloadBackupsTab(response?.data?.message || 'Backup deleted.');
            }).fail((response) => {
                $feedback
                    .text(getErrorMessage(response, 'The backup could not be deleted.'))
                    .addClass('is-error');
                $button.prop('disabled', false).text(originalText);
            });
        });
}
